import { equipItem, VAULT, isArmor, isCrafted, isLocked, isMasterwork, isWeapon, moveItem, type Rolled, type Stash, type StashItem } from './character';
import type { GodMark } from './godrolls';
import type { HubChar } from './hub';

/**
 * The Item Manager's rules, apart from its page: the board's slots and items (spots), the moves the game needs for a
 * drop or a click (plan), the best Power a character can wear (maxPowerSet), tags, and the search words (searchOf).
 */

export const SECTIONS: { title: string; slots: [number, string][] }[] = [
  { title: 'Weapons', slots: [[1498876634, 'Kinetic'], [2465295065, 'Energy'], [953998645, 'Power']] },
  { title: 'Armor', slots: [[3448274439, 'Helmet'], [3551918588, 'Gauntlets'], [14239492, 'Chest Armor'], [20886954, 'Leg Armor'], [1585787867, 'Class Item']] },
  { title: 'General', slots: [[4023194814, 'Ghost'], [2025709351, 'Sparrow'], [284967655, 'Ship']] },
];
export const SLOT_SET = new Set(SECTIONS.flatMap((s) => s.slots.map(([b]) => b)));
export const CLASSES = ['Titan', 'Hunter', 'Warlock'];

export const ARMOUR_STATS = ['Health', 'Melee', 'Grenade', 'Super', 'Class', 'Weapons', 'Mobility', 'Resilience', 'Recovery', 'Discipline', 'Intellect', 'Strength'];
export const armourTotal = (r: Rolled) => r.stats.filter((s) => ARMOUR_STATS.includes(s.name)).reduce((n, s) => n + s.value, 0);
export const TIER_NAMES: Record<number, string> = { 6: 'Exotic', 5: 'Legendary', 4: 'Rare', 3: 'Uncommon', 2: 'Common' };

/** Where an item is: a character (worn or carried) or the vault (owner null). */
export type Where = string | null;
export interface Spot { item: StashItem; owner: Where; worn: boolean }
export interface Step { label: string; run: (token: string) => Promise<void>; apply: (w: Spot[]) => Spot[] }

export const keyOf = (i: StashItem) => i.instanceId || `${i.hash}`;
export const SORTS: [string, string, (a: Spot, b: Spot) => number][] = [
  ['rarity', 'Rarity, then Power', (a, b) => b.item.tier - a.item.tier || (b.item.power ?? 0) - (a.item.power ?? 0) || a.item.name.localeCompare(b.item.name)],
  ['power', 'Power', (a, b) => (b.item.power ?? 0) - (a.item.power ?? 0) || b.item.tier - a.item.tier || a.item.name.localeCompare(b.item.name)],
  ['name', 'Name', (a, b) => a.item.name.localeCompare(b.item.name) || (b.item.power ?? 0) - (a.item.power ?? 0)],
  ['type', 'Weapon or armour type', (a, b) => a.item.typeName.localeCompare(b.item.typeName) || b.item.tier - a.item.tier || (b.item.power ?? 0) - (a.item.power ?? 0)],
];

export const kindOf = (i: StashItem) => (isWeapon(i) ? 'weapon' : isArmor(i) ? 'armour' : 'other');
export const exotic = (i: StashItem) => i.tier === 6;

export function spotsOf(stash: Stash): Spot[] {
  const list: Spot[] = [];
  for (const [cid, worn] of Object.entries(stash.equipped)) for (const i of worn) if (SLOT_SET.has(i.slot) && i.instanceId) list.push({ item: i, owner: cid, worn: true });
  for (const [cid, held] of Object.entries(stash.characters)) for (const i of held) if (SLOT_SET.has(i.bucket) && i.instanceId) list.push({ item: i, owner: cid, worn: false });
  for (const i of stash.account) if (i.bucket === VAULT && SLOT_SET.has(i.slot) && i.instanceId) list.push({ item: i, owner: null, worn: false });
  return list;
}

export const canWear = (i: StashItem, c?: HubChar) => !!c && (i.classType === 3 || i.classType === c.classType);

// ---------------- max Power: the best a character could wear from everything held ----------------

export const maxCache = new WeakMap<Spot[], Map<string, { items: Spot[]; power: number }>>();
export const POWER_SLOTS = [...SECTIONS[0].slots, ...SECTIONS[1].slots].map(([b]) => b);

/** The highest-Power item for each weapon and armour slot this character can wear, minding one exotic weapon and one exotic armour piece. */
export function maxPowerSet(spots: Spot[], c: HubChar): { items: Spot[]; power: number } {
  const best = (slot: number, noExotic: boolean) => spots.filter((s) => s.item.slot === slot && canWear(s.item, c) && s.item.power !== undefined && !(noExotic && exotic(s.item)))
    .sort((a, b) => (b.item.power ?? 0) - (a.item.power ?? 0) || Number(exotic(a.item)) - Number(exotic(b.item)))[0];
  const pick = (slots: number[]) => {
    // either no exotic at all, or the exotic in one slot and the rest without
    const options = [slots.map((sl) => best(sl, true)), ...slots.map((ex) => slots.map((sl) => best(sl, sl !== ex)))];
    const total = (o: (Spot | undefined)[]) => o.reduce((n, s) => n + (s?.item.power ?? 0), 0);
    return options.sort((a, b) => total(b) - total(a))[0];
  };
  const items = [...pick(POWER_SLOTS.slice(0, 3)), ...pick(POWER_SLOTS.slice(3))].filter((s): s is Spot => !!s);
  return { items, power: items.length === POWER_SLOTS.length ? Math.floor(items.reduce((n, s) => n + (s.item.power ?? 0), 0) / items.length) : 0 };
}

// ---------------- working out the moves ----------------

export interface Rules { chars: HubChar[]; slotRoom: (slot: number) => number; vaultRoom: number; vaultOthers: number; player: Parameters<typeof moveItem>[0] }

export function plan(start: Spot[], id: string, to: Where, wear: boolean, r: Rules): Step[] | string {
  let w = start;
  const steps: Step[] = [];
  const push = (s: Step) => { steps.push(s); w = s.apply(w); };
  const find = (k: string) => w.find((s) => keyOf(s.item) === k)!;
  const cls = (cid: Where) => r.chars.find((c) => c.id === cid)?.cls ?? 'character';
  const first = find(id);
  if (!first) return 'That item is no longer there. Click Read again.';
  const it = first.item;
  if (first.owner === to && first.worn === wear) return [];
  const toChar = r.chars.find((c) => c.id === to);
  if (wear && !canWear(it, toChar)) return `${it.name} is ${CLASSES[it.classType] ?? 'another class\'s'} armour: your ${cls(to)} cannot wear it.`;

  const equip = (s: Spot, cid: string): Step => ({
    label: `Equip ${s.item.name}`,
    run: (t) => equipItem(r.player, t, cid, s.item),
    apply: (list) => list.map((x) => keyOf(x.item) === keyOf(s.item) ? { ...x, worn: true }
      : x.owner === cid && x.worn && x.item.slot === s.item.slot ? { ...x, worn: false } : x),
  });
  const move = (s: Spot, from: Where, dest: Where): Step => ({
    label: `Move ${s.item.name} to ${dest ? `your ${cls(dest)}` : 'the vault'}`,
    run: (t) => moveItem(r.player, t, s.item, from, dest),
    apply: (list) => list.map((x) => keyOf(x.item) === keyOf(s.item) ? { ...x, owner: dest, worn: false } : x),
  });
  // something else for a character to wear in a slot: the best they carry (no exotic, when one of that kind is worn elsewhere)
  const stand = (cid: string, slot: number, leaving: StashItem) => {
    const c = r.chars.find((x) => x.id === cid);
    const otherExotic = w.some((x) => x.owner === cid && x.worn && x.item.slot !== slot && exotic(x.item) && kindOf(x.item) === kindOf(leaving));
    return w.filter((x) => x.owner === cid && !x.worn && x.item.slot === slot && keyOf(x.item) !== keyOf(leaving) && canWear(x.item, c) && !(otherExotic && exotic(x.item)))
      .sort((a, b) => Number(exotic(a.item)) - Number(exotic(b.item)) || (b.item.power ?? 0) - (a.item.power ?? 0))[0];
  };
  const vaultUsed = () => r.vaultOthers + w.filter((x) => x.owner === null).length;

  // 1. worn and leaving: put something else on first
  if (first.worn && first.owner !== to) {
    const sub = stand(first.owner!, it.slot, it);
    if (!sub) return `Your ${cls(first.owner)} is wearing ${it.name} and carries nothing else to wear in that slot. Move another item there first.`;
    push(equip(sub, first.owner!));
  }
  // 2. the move itself, making room on the way
  const here = find(id);
  if (here.owner !== to) {
    if (to !== null) {
      const carried = w.filter((x) => x.owner === to && !x.worn && x.item.slot === it.slot);
      if (carried.length >= r.slotRoom(it.slot)) {
        const out = [...carried].sort((a, b) => Number(isLocked(a.item)) - Number(isLocked(b.item)) || (a.item.power ?? 0) - (b.item.power ?? 0))[0];
        if (vaultUsed() >= r.vaultRoom) return `Your ${cls(to)} has no room in that slot and your vault is full.`;
        push(move(out, to, null));
      }
    }
    if (here.owner !== null && vaultUsed() >= r.vaultRoom) return 'Your vault is full: there is no room to pass the item through it.';
    push(move(here, here.owner, to));
  }
  // 3. equip, minding the one-exotic rule
  if (wear && to) {
    if (exotic(it)) {
      const other = w.find((x) => x.owner === to && x.worn && exotic(x.item) && x.item.slot !== it.slot && kindOf(x.item) === kindOf(it));
      if (other) {
        const sub = stand(to, other.item.slot, other.item);
        if (!sub || exotic(sub.item)) return `Your ${cls(to)} already wears the exotic ${other.item.name} and carries no other ${kindOf(it) === 'weapon' ? 'weapon' : 'armour'} for that slot to take its place.`;
        push(equip(sub, to));
      }
    }
    push(equip(find(id), to));
  }
  return steps;
}

// ---------------- tags (kept in this browser, per Bungie account) ----------------

export type Tag = 'favorite' | 'keep' | 'junk' | 'infuse' | 'archive';
export const TAGS: [Tag, string, string][] = [['favorite', 'Favourite', '★'], ['keep', 'Keep', '✔'], ['junk', 'Junk', '✖'], ['infuse', 'Infuse', '⇪'], ['archive', 'Archive', '▤']];

/** What the search and the squares know beyond the item itself. */
export interface Marks { tagOf: (id: string) => Tag | undefined; noteOf: (id: string) => string | undefined; godOf: (i: StashItem) => GodMark | undefined; isNew: (id: string) => boolean; copiesOf: (hash: number) => number }
// ---------------- searching (a small part of DIM's search words) ----------------

export const ELEMENTS: Record<string, number> = { kinetic: 1, arc: 2, solar: 3, void: 4, stasis: 6, strand: 7 };
export const AMMO: Record<string, number> = { primary: 1, special: 2, heavy: 3 };
export const TIERS: Record<string, number> = { exotic: 6, legendary: 5, rare: 4, uncommon: 3, common: 2 };
export const SEARCH_WORDS: [string, string][] = [
  ['is:weapon', 'Weapons'], ['is:armor', 'Armour'], ['is:exotic', 'Exotics'], ['is:legendary', 'Legendaries'], ['is:godroll', 'God rolls (PvE or PvP)'],
  ['is:pvegodroll', 'PvE god rolls'], ['is:pvpgodroll', 'PvP god rolls'], ['is:dupe', 'Items you have more than one of'], ['is:dupelower', 'The lesser copies (not locked, less Power)'],
  ['is:junk', 'Tagged junk'], ['is:favorite', 'Tagged favourite'], ['is:tagged', 'Any tag'], ['is:noted', 'Has a note'], ['notes:raid', 'Note says "raid"'], ['is:locked', 'Locked'], ['-is:locked', 'Not locked'],
  ['is:masterwork', 'Masterworked'], ['is:crafted', 'Shaped (crafted)'], ['is:maxpower', 'Highest Power in its slot'], ['power:>=550', 'Power 550 or more'],
  ['is:new', 'New since you last looked'], ['is:equipped', 'Worn now'], ['is:vault', 'In the vault'], ['is:arc', 'Arc'], ['is:solar', 'Solar'], ['is:void', 'Void'], ['is:stasis', 'Stasis'], ['is:strand', 'Strand'],
  ['is:kinetic', 'Kinetic'], ['is:primary', 'Primary ammo'], ['is:special', 'Special ammo'], ['is:heavy', 'Heavy ammo'],
];
export const SEARCH_HELP = 'Words: is:weapon, is:armor, is:exotic, is:legendary, is:locked, is:unlocked, is:masterwork, is:crafted, is:dupe, is:equipped, is:vault, '
  + 'is:arc / solar / void / stasis / strand / kinetic, is:primary / special / heavy, is:titan / hunter / warlock, is:maxpower, power:>540, power:<300, '
  + 'is:godroll, is:pvegodroll, is:pvpgodroll, is:dupelower, is:junk / favorite / keep / infuse / archive, is:tagged. '
  + 'Put - in front to turn one round (-is:locked). Anything else matches the name or type.';

/** The query as one test for each item: every word must hold. */
export function searchOf(query: string, spots: Spot[], marks: Marks): (s: Spot) => boolean {
  const words = query.toLowerCase().match(/-?\w+:[<>=]*[\w.]+|-?"[^"]*"|\S+/g) ?? [];
  if (!words.length) return () => true;
  const copies = new Map<number, number>();
  for (const s of spots) copies.set(s.item.hash, (copies.get(s.item.hash) ?? 0) + 1);
  const best = new Map<number, number>();
  for (const s of spots) best.set(s.item.slot, Math.max(best.get(s.item.slot) ?? 0, s.item.power ?? 0));
  // the copy of each item worth keeping (tagged keep or favourite, then locked, then the most Power): the rest are the lower dupes
  const kept = (s: Spot) => Number(['keep', 'favorite'].includes(marks.tagOf(keyOf(s.item)) ?? ''));
  const keepers = new Map<number, string>();
  for (const s of [...spots].sort((a, b) => kept(b) - kept(a) || Number(isLocked(b.item)) - Number(isLocked(a.item)) || (b.item.power ?? 0) - (a.item.power ?? 0))) if (!keepers.has(s.item.hash)) keepers.set(s.item.hash, keyOf(s.item));
  const tests = words.map((raw) => {
    const not = raw.startsWith('-') && raw.length > 1;
    const w = (not ? raw.slice(1) : raw).replace(/"/g, '');
    let t: (s: Spot) => boolean;
    const [k, v = ''] = w.split(':');
    if (k === 'is' && v) {
      t = ({ item: i, owner, worn }) => {
        if (v in ELEMENTS) return isWeapon(i) && i.damage === ELEMENTS[v];
        if (v in AMMO) return isWeapon(i) && i.ammo === AMMO[v];
        if (v in TIERS) return i.tier === TIERS[v];
        const cls = CLASSES.findIndex((c) => c.toLowerCase() === v);
        if (cls >= 0) return isArmor(i) && i.classType === cls;
        switch (v) {
          case 'weapon': return isWeapon(i);
          case 'armor': case 'armour': return isArmor(i);
          case 'locked': return isLocked(i);
          case 'unlocked': return !isLocked(i);
          case 'masterwork': case 'mw': return isMasterwork(i);
          case 'crafted': case 'shaped': return isCrafted(i);
          case 'dupe': return (copies.get(i.hash) ?? 0) > 1;
          case 'dupelower': return (copies.get(i.hash) ?? 0) > 1 && keepers.get(i.hash) !== keyOf(i);
          case 'equipped': case 'worn': return worn;
          case 'vault': return owner === null;
          case 'maxpower': return i.power !== undefined && i.power === best.get(i.slot);
          case 'tagged': return !!marks.tagOf(keyOf(i));
          case 'untagged': return !marks.tagOf(keyOf(i));
          case 'noted': return !!marks.noteOf(keyOf(i));
          case 'new': return marks.isNew(keyOf(i));
          case 'favorite': case 'favourite': case 'keep': case 'junk': case 'infuse': case 'archive': return marks.tagOf(keyOf(i)) === v.replace('favourite', 'favorite');
          case 'godroll': case 'wishlist': return !!marks.godOf(i);
          case 'pvegodroll': case 'pvegod': case 'pve': return !!marks.godOf(i)?.pve;
          case 'pvpgodroll': case 'pvpgod': case 'pvp': return !!marks.godOf(i)?.pvp;
          default: return false;
        }
      };
    } else if ((k === 'notes' || k === 'note') && v) {
      t = ({ item: i }) => (marks.noteOf(keyOf(i)) ?? '').toLowerCase().includes(v);
    } else if ((k === 'power' || k === 'light') && v) {
      const m = v.match(/^(<=|>=|<|>|=)?(\d+)$/);
      const n = Number(m?.[2] ?? NaN), op = m?.[1] ?? '=';
      t = ({ item: i }) => i.power !== undefined && (op === '>' ? i.power > n : op === '>=' ? i.power >= n : op === '<' ? i.power < n : op === '<=' ? i.power <= n : i.power === n);
    } else {
      t = ({ item: i }) => i.name.toLowerCase().includes(w) || i.typeName.toLowerCase().includes(w) || (marks.noteOf(keyOf(i)) ?? '').toLowerCase().includes(w);
    }
    return not ? (s: Spot) => !t(s) : t;
  });
  return (s) => tests.every((t) => t(s));
}

// every is: word the search understands (for telling the user about one it does not)
const IS_WORDS = new Set([...Object.keys(ELEMENTS), ...Object.keys(AMMO), ...Object.keys(TIERS), ...CLASSES.map((c) => c.toLowerCase()),
  'weapon', 'armor', 'armour', 'locked', 'unlocked', 'masterwork', 'mw', 'crafted', 'shaped', 'dupe', 'dupelower', 'equipped', 'worn', 'vault', 'maxpower',
  'tagged', 'untagged', 'noted', 'new', 'favorite', 'favourite', 'keep', 'junk', 'infuse', 'archive', 'godroll', 'wishlist', 'pvegodroll', 'pvegod', 'pve',
  'pvpgodroll', 'pvpgod', 'pvp']);

/** The is: words in a query the search does not know (they match nothing). */
export const unknownWords = (query: string) => (query.toLowerCase().match(/-?is:[\w]+/g) ?? []).filter((w) => !IS_WORDS.has(w.replace(/^-?is:/, '')));

// ---------------- groups: how the vault's part of a slot can be split, each group foldable ----------------

const ELEMENT_NAMES: Record<number, string> = { 1: 'Kinetic', 2: 'Arc', 3: 'Solar', 4: 'Void', 6: 'Stasis', 7: 'Strand' };
const AMMO_NAMES: Record<number, string> = { 1: 'Primary', 2: 'Special', 3: 'Heavy' };

/** One way of grouping: for an item, its group's key, name and place (lower first). */
export type Grouper = (s: Spot, marks: Marks) => [key: string, name: string, order: number];
export const GROUPS: [string, string, Grouper | null][] = [
  ['none', 'Nothing (one list)', null],
  ['rarity', 'Rarity', ({ item: i }) => [`r${i.tier}`, TIER_NAMES[i.tier] ?? 'Other', -i.tier]],
  ['god', 'God roll', ({ item: i }, m) => {
    const g = m.godOf(i);
    return !g ? ['g0', 'Not a god roll', 9] : g.pve && g.pvp ? ['g3', 'PvE and PvP god roll', 0] : g.pve ? ['g1', 'PvE god roll', 1] : g.pvp ? ['g2', 'PvP god roll', 2] : ['g4', 'Community pick', 3];
  }],
  ['tag', 'Tag', ({ item: i }, m) => { const t = m.tagOf(keyOf(i)); const at = TAGS.findIndex(([k]) => k === t); return t ? [`t${t}`, TAGS[at][1], at] : ['t-', 'Not tagged', 99]; }],
  ['element', 'Element', ({ item: i }) => (isWeapon(i) ? [`e${i.damage}`, ELEMENT_NAMES[i.damage] ?? 'Other', i.damage] : ['e-', 'No element', 99])],
  ['ammo', 'Ammo', ({ item: i }) => (isWeapon(i) ? [`a${i.ammo}`, AMMO_NAMES[i.ammo] ?? 'Other', i.ammo] : ['a-', 'No ammo', 99])],
  ['type', 'Weapon or armour type', ({ item: i }) => [`y${i.typeName}`, i.typeName || 'Other', 0]],
  // only items held three times or more get their own group; the rest share one at the end (folded at first)
  ['copies', 'Copies (3 or more of an item)', ({ item: i }, m) => { const n = m.copiesOf(i.hash); return n >= 3 ? [`c${i.hash}`, `${i.name} (${n} held)`, -n] : ['c-', 'Fewer than 3 copies', 1e9]; }],
];

/** The items split into groups, in order (names breaking ties). */
export function groupSpots(list: Spot[], by: Grouper, marks: Marks): { key: string; name: string; items: Spot[] }[] {
  const m = new Map<string, { key: string; name: string; order: number; items: Spot[] }>();
  for (const s of list) {
    const [key, name, order] = by(s, marks);
    (m.get(key) ?? m.set(key, { key, name, order, items: [] }).get(key)!).items.push(s);
  }
  return [...m.values()].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
}
